import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { MeetingPlugins } from '../components/MeetingPlugins';
import { AIProviderCard } from '../components/AIProviderCard';
import { ModulesTab } from '../components/admin/ModulesTab';
import { BroadcastTab } from '../components/admin/BroadcastTab';
import { BrandingCard } from '../components/admin/BrandingCard';
import { TenantSettingsCard } from '../components/admin/TenantSettingsCard';
import { IndiaPackCard } from '../components/admin/IndiaPackCard';
import { MarketplaceTab } from '../components/admin/MarketplaceTab';

const STAFF_ROLES = ['school_admin', 'principal', 'registrar', 'admissions_officer', 'teacher', 'finance_admin'];

const Badge = ({ children, tone = 'slate' }) => (
  <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded bg-${tone}-100 text-${tone}-700`}>{children}</span>
);

export default function Admin() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: checklist } = useQuery({ queryKey: ['checklist'], queryFn: () => api.get('/admin/checklist').then((r) => r.data) });
  const { data: tenants } = useQuery({ queryKey: ['tenants'], queryFn: () => api.get('/tenants').then((r) => r.data) });
  const { data: tenancyStatus } = useQuery({ queryKey: ['tenancy-status'], queryFn: () => api.get('/tenancy/status').then((r) => r.data).catch(() => []) });
  const migrateMut = useMutation({
    mutationFn: (body) => api.post('/tenancy/migrate', body),
    onSuccess: (r) => { toast.success(`Moved to a ${r.data.mode} database - ${Object.keys(r.data.collections).length} collections verified by checksum`); qc.invalidateQueries({ queryKey: ['tenancy-status'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const { data: users } = useQuery({ queryKey: ['users'], queryFn: () => api.get('/users').then((r) => r.data) });
  const { data: policies } = useQuery({ queryKey: ['policies'], queryFn: () => api.get('/policies').then((r) => r.data) });
  const { data: auditLog } = useQuery({ queryKey: ['audit'], queryFn: () => api.get('/audit', { params: { limit: 100 } }).then((r) => r.data) });
  const { data: consents } = useQuery({ queryKey: ['consents'], queryFn: () => api.get('/consents').then((r) => r.data) });
  const { data: registry } = useQuery({ queryKey: ['registry'], queryFn: () => api.get('/ai/registry').then((r) => r.data) });
  const { data: bgReviews } = useQuery({ queryKey: ['bg'], queryFn: () => api.get('/iam/break-glass/reviews').then((r) => r.data) });
  const school = (tenants || []).find((t) => t.type === 'school');
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState(null);
  const [bgReason, setBgReason] = useState('');
  const [staffOpen, setStaffOpen] = useState(false);
  const [staffForm, setStaffForm] = useState({ name: '', email: '', role: 'teacher' });
  const { data: emailCfg } = useQuery({ queryKey: ['email-settings'], queryFn: () => api.get('/settings/email').then((r) => r.data) });
  const [emailForm, setEmailForm] = useState({ api_key: '', sender_email: '' });
  const [rolloverPlan, setRolloverPlan] = useState(null);
  const [toYear, setToYear] = useState('2027-28');

  const saveEmailMut = useMutation({
    mutationFn: () => api.post('/settings/email', { api_key: emailForm.api_key || null, sender_email: emailForm.sender_email || emailCfg?.sender_email }),
    onSuccess: (r) => { toast.success(r.data.configured ? 'Email credentials saved - live sending enabled' : 'Sender saved - add the API key to go live'); setEmailForm({ api_key: '', sender_email: '' }); qc.invalidateQueries({ queryKey: ['email-settings'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const testEmailMut = useMutation({
    mutationFn: () => api.post('/settings/email/test'),
    onSuccess: (r) => toast[r.data.status === 'sent' ? 'success' : 'warning'](`Test email to ${r.data.to}: ${r.data.status}`),
    onError: (e) => toast.error(errMsg(e)),
  });
  const { data: lastRollover } = useQuery({ queryKey: ['last-rollover'], queryFn: () => api.get('/academics/rollover/last', { params: { tenant_id: user?.tenant_id || school?.id } }).then((r) => r.data), enabled: !!(user?.tenant_id || school?.id) });
  const undoRolloverMut = useMutation({
    mutationFn: () => api.post('/academics/rollover/undo', { tenant_id: user?.tenant_id || school?.id }),
    onSuccess: (r) => { toast.success(`Rollover undone - ${r.data.restored} students restored to their previous grade`); qc.invalidateQueries(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const previewRolloverMut = useMutation({
    mutationFn: () => api.post('/academics/rollover/preview', { tenant_id: user?.tenant_id || school?.id, to_year: toYear }),
    onSuccess: (r) => setRolloverPlan(r.data), onError: (e) => toast.error(errMsg(e)),
  });
  const commitRolloverMut = useMutation({
    mutationFn: () => api.post('/academics/rollover/commit', { tenant_id: user?.tenant_id || school?.id, to_year: toYear }),
    onSuccess: (r) => { toast.success(`Rollover done - ${r.data.promoted} promoted, ${r.data.graduated} graduated`); setRolloverPlan(null); qc.invalidateQueries(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const createStaffMut = useMutation({
    mutationFn: () => api.post('/users', { ...staffForm, tenant_id: user?.tenant_id || school?.id }),
    onSuccess: () => { toast.success(`${staffForm.name} added - temp password Welcome@123`); setStaffOpen(false); setStaffForm({ name: '', email: '', role: 'teacher' }); qc.invalidateQueries({ queryKey: ['users'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const patchUserMut = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/users/${id}`, patch),
    onSuccess: () => { toast.success('User updated'); qc.invalidateQueries({ queryKey: ['users'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const parseRows = () => csv.trim().split('\n').filter(Boolean).map((line) => {
    const [name, grade, section, dob] = line.split(',').map((x) => (x || '').trim());
    return { name, grade, section: section || 'A', dob: dob || '' };
  });

  const previewMut = useMutation({
    mutationFn: () => api.post('/admin/import/students/preview', { tenant_id: school?.id, rows: parseRows() }),
    onSuccess: (r) => setPreview(r.data), onError: (e) => toast.error(errMsg(e)),
  });
  const commitMut = useMutation({
    mutationFn: () => api.post('/admin/import/students/commit', { tenant_id: school?.id, rows: parseRows() }),
    onSuccess: (r) => { toast.success(`Imported ${r.data.imported}, skipped ${r.data.skipped} dupes. Rollback available.`); setPreview(null); setCsv(''); qc.invalidateQueries({ queryKey: ['checklist'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const verifyMut = useMutation({
    mutationFn: () => api.get('/audit/verify'),
    onSuccess: (r) => toast[r.data.intact ? 'success' : 'error'](`Audit chain: ${r.data.checked} entries checked - ${r.data.intact ? 'INTACT (tamper-evident)' : `BROKEN at seq ${r.data.broken_at}`}`),
  });
  const bgMut = useMutation({
    mutationFn: () => api.post('/iam/break-glass', { reason: bgReason, duration_minutes: 30 }),
    onSuccess: () => { toast.warning('Break-glass activated for 30 min. Principal + admin alerted. Review is mandatory.'); setBgReason(''); qc.invalidateQueries({ queryKey: ['bg'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const bgReviewMut = useMutation({
    mutationFn: (id) => api.post(`/iam/break-glass/reviews/${id}/complete`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bg'] }),
  });
  const resetDemoMut = useMutation({
    mutationFn: () => api.post('/admin/demo-tenant/reset'),
    onSuccess: () => { toast.success('Demo tenant regenerated with fresh synthetic data'); qc.invalidateQueries(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div data-testid="admin-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">Admin Center</h1>
      <Tabs defaultValue="checklist">
        <TabsList className="h-auto flex-wrap">
          {['checklist', 'tenants', 'users', 'modules', 'broadcast', 'policies', 'consents', 'audit', 'ai-governance', 'import', 'rollover', 'plugins', 'marketplace', 'settings'].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="capitalize h-auto py-2">{t.replace('-', ' ')}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="checklist" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-md p-5 max-w-2xl" data-testid="golive-checklist">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="font-display text-lg font-semibold">Go-live readiness</h2>
              <span className={`font-mono-label text-[10px] uppercase px-3 py-1 rounded ${checklist?.production_ready ? 'bg-green-100 text-green-800' : 'bg-rose-100 text-rose-700'}`} data-testid="golive-status">
                {checklist?.production_ready ? 'Ready for production' : 'Production promotion blocked'}
              </span>
            </div>
            {(checklist?.checks || []).map((c) => (
              <div key={c.key} className="flex items-center gap-3 py-2 border-t border-slate-100 text-sm" data-testid={`check-${c.key}`}>
                <span className={`w-5 h-5 rounded flex items-center justify-center text-xs ${c.done ? 'bg-green-700 text-white' : 'bg-slate-200 text-slate-500'}`}>{c.done ? '✓' : '·'}</span>
                {c.label}
              </div>
            ))}
            <p className="text-xs text-slate-400 mt-3">{checklist?.note}</p>
          </div>
          {user?.role === 'global_admin' && (
            <div className="mt-6 max-w-2xl bg-white border border-slate-200 rounded-md p-5">
              <h3 className="font-display font-semibold mb-2">Demo tenant generator</h3>
              <p className="text-sm text-slate-500 mb-3">Synthetic data only - demo tenants cannot read live tenants (subtree isolation).</p>
              <Button data-testid="demo-reset-button" onClick={() => resetDemoMut.mutate()} disabled={resetDemoMut.isPending} variant="outline">Reset demo tenant</Button>
            </div>
          )}
          <div className="mt-6 max-w-2xl bg-white border border-rose-200 rounded-md p-5">
            <h3 className="font-display font-semibold mb-2 text-rose-700">Break-glass emergency access</h3>
            <p className="text-sm text-slate-500 mb-3">Time-bound (max 60 min). Alerts principal + tenant admin in real time. Mandatory review record.</p>
            <div className="flex gap-2">
              <Input data-testid="breakglass-reason-input" placeholder="Reason (mandatory)" value={bgReason} onChange={(e) => setBgReason(e.target.value)} />
              <Button data-testid="breakglass-activate-button" disabled={!bgReason} onClick={() => bgMut.mutate()} className="bg-rose-600 hover:bg-rose-500 shrink-0">Activate</Button>
            </div>
            {(bgReviews || []).length > 0 && (
              <div className="mt-4 divide-y divide-slate-100" data-testid="breakglass-reviews">
                {bgReviews.map((r) => (
                  <div key={r.id} className="py-2 text-sm flex flex-wrap items-center gap-2">
                    <span className="font-medium">{r.user_email}</span>
                    <span className="text-slate-500">{r.reason}</span>
                    <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded ${r.status === 'reviewed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{r.status.replace(/_/g, ' ')}</span>
                    {r.status === 'pending_review' && <button data-testid={`bg-review-${r.id}`} onClick={() => bgReviewMut.mutate(r.id)} className="text-xs text-blue-600 hover:underline ml-auto">Mark reviewed</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="tenants" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4" data-testid="tenants-list">
            {(tenants || []).map((t) => {
              const tm = (tenancyStatus || []).find((x) => x.id === t.id);
              return (
              <div key={t.id} className="bg-white border border-slate-200 rounded-md p-4 stagger-in">
                <p className="font-display font-bold">{t.name}</p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Badge>{t.type}</Badge><Badge>{t.country} pack</Badge><Badge>{t.region}</Badge>
                  {t.data_residency_locked && <Badge tone="green">residency locked</Badge>}
                  {t.is_demo && <Badge tone="amber">demo</Badge>}
                  {tm && <Badge tone={tm.db_mode === 'dedicated' ? 'green' : undefined}>{tm.db_mode === 'dedicated' ? 'dedicated database' : 'shared database'}</Badge>}
                </div>
                <p className="font-mono-label text-[9px] text-slate-400 mt-2">path: {t.path}</p>
                {tm && !user?.tenant_id && (
                  <button data-testid={`tenancy-toggle-${t.id}`} className="text-xs text-blue-600 underline mt-2"
                    disabled={migrateMut.isPending}
                    onClick={() => window.confirm(`Move ${t.name} to a ${tm.db_mode === 'dedicated' ? 'shared' : 'dedicated'} database? Writes freeze briefly; the copy is verified with checksums before cutover.`)
                      && migrateMut.mutate({ tenant_id: t.id, mode: tm.db_mode === 'dedicated' ? 'shared' : 'dedicated' })}>
                    {migrateMut.isPending ? 'Migrating…' : tm.db_mode === 'dedicated' ? 'Move to the shared database' : 'Move to a dedicated database'}
                  </button>
                )}
              </div>
            );})}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <p className="text-sm text-slate-500">Joiner / mover / leaver lifecycle - every change is audited.</p>
            <Dialog open={staffOpen} onOpenChange={setStaffOpen}>
              <DialogTrigger asChild><Button data-testid="add-staff-button" className="bg-slate-900 hover:bg-slate-800">Add staff</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display">Add staff member</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Input data-testid="staff-name-input" placeholder="Full name" value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} />
                  <Input data-testid="staff-email-input" placeholder="Email" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} />
                  <select data-testid="staff-role-select" value={staffForm.role} onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                    {STAFF_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                  </select>
                  <Button data-testid="staff-create-button" disabled={!staffForm.name || !staffForm.email || createStaffMut.isPending} onClick={() => createStaffMut.mutate()} className="w-full bg-slate-900 hover:bg-slate-800">Create account</Button>
                  <p className="text-xs text-slate-400">Temporary password: Welcome@123 - user should enable MFA from Security.</p>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="users-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr className="text-left">{['Name', 'Email', 'Role', 'Status', 'MFA', 'Actions'].map((h) => <th key={h} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{h}</th>)}</tr></thead>
              <tbody>{(users || []).map((u, i) => (
                <tr key={u.id} className={`border-t border-slate-100 ${i % 2 ? 'bg-slate-50/50' : ''}`}>
                  <td className="py-2 px-3 font-medium whitespace-nowrap">{u.name}</td><td className="py-2 px-3">{u.email}</td>
                  <td className="py-2 px-3">
                    {u.id !== user?.id && STAFF_ROLES.includes(u.role) ? (
                      <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => patchUserMut.mutate({ id: u.id, patch: { role: e.target.value } })}
                        className="border border-slate-200 rounded px-2 py-1 text-xs bg-white capitalize">
                        {STAFF_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                      </select>
                    ) : <span className="capitalize">{u.role?.replace(/_/g, ' ')}</span>}
                  </td>
                  <td className="py-2 px-3"><span className={`text-xs rounded px-2 py-0.5 ${u.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-rose-100 text-rose-700'}`}>{u.status}</span></td>
                  <td className="py-2 px-3">{u.mfa_enabled ? '✓' : '-'}</td>
                  <td className="py-2 px-3">
                    {u.id !== user?.id && (
                      u.status === 'active' ? (
                        <button data-testid={`suspend-${u.id}`} onClick={() => patchUserMut.mutate({ id: u.id, patch: { status: 'suspended' } })}
                          className="text-xs border border-rose-200 text-rose-600 rounded px-2 py-1 hover:bg-rose-50 transition-colors">Suspend</button>
                      ) : (
                        <button data-testid={`activate-${u.id}`} onClick={() => patchUserMut.mutate({ id: u.id, patch: { status: 'active' } })}
                          className="text-xs border border-green-200 text-green-700 rounded px-2 py-1 hover:bg-green-50 transition-colors">Activate</button>
                      )
                    )}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="policies" className="mt-4">
          <p className="text-sm text-slate-500 mb-3">One policy model across access, consent, retention & AI. Hierarchy inheritance, most-restrictive-wins - a child tenant can never relax a parent deny.</p>
          <div className="space-y-2" data-testid="policies-list">
            {(policies || []).map((p) => (
              <div key={p.id} className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-2 text-sm">
                <Badge tone={p.effect === 'deny' ? 'rose' : 'green'}>{p.effect}</Badge>
                <Badge>{p.domain}</Badge>
                <span className="font-medium">{p.action}</span>
                <span className="text-slate-500">{p.description}</span>
                <span className="font-mono-label text-[9px] text-slate-400 ml-auto">v{p.version} · {p.tenant_id ? 'tenant' : 'global'}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="consents" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="consents-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr className="text-left">{['Student', 'Type', 'Status', 'Set by'].map((h) => <th key={h} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{h}</th>)}</tr></thead>
              <tbody>{(consents || []).map((c, i) => (
                <tr key={c.id} className={`border-t border-slate-100 ${i % 2 ? 'bg-slate-50/50' : ''}`}>
                  <td className="py-2 px-3 font-medium">{c.student_name}</td>
                  <td className="py-2 px-3 capitalize">{c.type.replace(/_/g, ' ')}</td>
                  <td className="py-2 px-3"><span className={`text-xs rounded px-2 py-0.5 ${c.status === 'granted' ? 'bg-green-100 text-green-800' : 'bg-rose-100 text-rose-700'}`}>{c.status}</span></td>
                  <td className="py-2 px-3 text-slate-500">{c.set_by} ({c.set_by_role})</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <Button data-testid="audit-verify-button" onClick={() => verifyMut.mutate()} variant="outline" className="mb-4">Verify hash chain</Button>
          <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100 max-h-[600px] overflow-auto" data-testid="audit-log">
            {(auditLog || []).map((a) => (
              <div key={a.id} className="p-3 text-sm flex flex-wrap items-center gap-2">
                <span className="font-mono-label text-[10px] text-slate-400">#{a.seq} · {a.ts?.slice(0, 19)}</span>
                <span className="font-medium">{a.action}</span>
                <span className="text-slate-500">{a.actor_email}</span>
                {a.ai_involved && <Badge tone="blue">AI</Badge>}
                {a.reason && <span className="text-xs text-slate-400 italic">"{a.reason}"</span>}
                <span className="font-mono-label text-[9px] text-slate-300 ml-auto hidden lg:inline">{a.hash?.slice(0, 12)}...</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="ai-governance" className="mt-4">
          <p className="text-sm text-slate-500 mb-3">AI governance registry - every model & agent has a risk class, owner and approval. AI passes through the same policy engine as humans; there is no bypass.</p>
          <div className="grid md:grid-cols-3 gap-4" data-testid="ai-registry">
            {(registry || []).map((r) => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-md p-4 stagger-in">
                <p className="font-mono-label text-xs font-semibold">{r.name}</p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Badge>{r.kind}</Badge>
                  <Badge tone={r.risk_class === 'high' ? 'rose' : r.risk_class === 'medium' ? 'amber' : 'green'}>{r.risk_class} risk</Badge>
                  {r.approved && <Badge tone="green">approved</Badge>}
                </div>
                <p className="text-xs text-slate-400 mt-2">owner: {r.owner} · provider: {r.provider}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="import" className="mt-4">
          <div className="max-w-2xl bg-white border border-slate-200 rounded-md p-5" data-testid="import-panel">
            <h2 className="font-display text-lg font-semibold mb-1">CSV student import</h2>
            <p className="text-sm text-slate-500 mb-3">Paste rows as <code className="font-mono-label text-xs">name, grade, section, dob</code> - validate - dedupe - preview - commit (rollback supported).</p>
            <textarea data-testid="import-csv-input" value={csv} onChange={(e) => setCsv(e.target.value)}
              placeholder={'Ravi Kumar, 5, A, 2015-04-12\nSneha Pillai, 6, A, 2014-08-03'}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm h-32 font-mono-label focus:outline-none focus:ring-2 focus:ring-slate-900" />
            <div className="flex gap-2 mt-3">
              <Button data-testid="import-preview-button" variant="outline" disabled={!csv.trim()} onClick={() => previewMut.mutate()}>Preview</Button>
              <Button data-testid="import-commit-button" disabled={!preview || preview.valid.length === 0} onClick={() => commitMut.mutate()} className="bg-slate-900 hover:bg-slate-800">Commit {preview ? `${preview.valid.length} rows` : ''}</Button>
            </div>
            {preview && (
              <div className="mt-4 text-sm" data-testid="import-preview-result">
                <p className="text-green-700">{preview.valid.length} valid</p>
                <p className="text-amber-600">{preview.duplicates.length} duplicates skipped {preview.duplicates.map((d) => d.name).join(', ')}</p>
                <p className="text-rose-600">{preview.errors.length} errors {preview.errors.map((e) => `row ${e.row}: ${e.error}`).join('; ')}</p>
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="rollover" className="mt-4">
          <div className="max-w-2xl bg-white border border-slate-200 rounded-md p-5" data-testid="rollover-panel">
            <h2 className="font-display text-lg font-semibold mb-1">School year rollover</h2>
            <p className="text-sm text-slate-500 mb-4">Promote all active students to the next grade in one guided step. Grade 12 students graduate to alumni. Fully audited.</p>
            {lastRollover?.exists && (
              <div className="border border-amber-200 bg-amber-50 rounded-md p-3 mb-4 flex flex-wrap items-center gap-2 text-sm" data-testid="last-rollover-info">
                <span>Last rollover: <b>{lastRollover.students} students - {lastRollover.to_year}</b> by {lastRollover.by} on {lastRollover.at?.slice(0, 10)}</span>
                <Button data-testid="rollover-undo-button" size="sm" variant="outline" className="ml-auto border-amber-400 text-amber-800 hover:bg-amber-100"
                  onClick={() => { if (window.confirm('Undo the last rollover? Every promoted student returns to their previous grade (from the saved history).')) undoRolloverMut.mutate(); }}>
                  Undo last rollover
                </Button>
              </div>
            )}
            <div className="flex gap-2 items-center mb-4">
              <span className="text-sm text-slate-600">New academic year:</span>
              <Input data-testid="rollover-year-input" value={toYear} onChange={(e) => setToYear(e.target.value)} className="w-32" />
              <Button data-testid="rollover-preview-button" variant="outline" onClick={() => previewRolloverMut.mutate()}>Preview</Button>
            </div>
            {rolloverPlan && (
              <div data-testid="rollover-plan">
                <table className="w-full text-sm border border-slate-200 rounded-md">
                  <thead className="bg-slate-50"><tr className="text-left">{['From', 'To', 'Students', 'Action'].map((h) => <th key={h} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{h}</th>)}</tr></thead>
                  <tbody>{rolloverPlan.plan.map((p) => (
                    <tr key={p.from_grade} className="border-t border-slate-100">
                      <td className="py-2 px-3">Grade {p.from_grade}</td><td className="py-2 px-3 font-medium">{p.to_grade === 'graduated' ? 'Alumni 🎓' : `Grade ${p.to_grade}`}</td>
                      <td className="py-2 px-3">{p.students}</td><td className="py-2 px-3 capitalize">{p.action}</td>
                    </tr>
                  ))}</tbody>
                </table>
                <div className="flex items-center gap-3 mt-4">
                  <Button data-testid="rollover-commit-button" onClick={() => commitRolloverMut.mutate()} disabled={commitRolloverMut.isPending} className="bg-rose-600 hover:bg-rose-500">Promote {rolloverPlan.total} students - {rolloverPlan.to_year}</Button>
                  <button className="text-xs text-slate-500 hover:text-slate-900" onClick={() => setRolloverPlan(null)}>Cancel</button>
                </div>
                <p className="text-xs text-amber-600 mt-2">This changes every active student's grade. Review the preview carefully - the action is audited and visible in the audit log.</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="modules" className="mt-4">
          <ModulesTab tenantId={user?.tenant_id || school?.id} role={user?.role} />
        </TabsContent>

        <TabsContent value="broadcast" className="mt-4">
          <BroadcastTab tenantId={user?.tenant_id || school?.id} />
        </TabsContent>

        <TabsContent value="plugins" className="mt-4">
          <div className="space-y-4">
            <MeetingPlugins tenantId={user?.tenant_id || school?.id} />
            <AIProviderCard tenantId={user?.tenant_id || school?.id} />
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <div className="max-w-2xl bg-white border border-slate-200 rounded-md p-5" data-testid="email-settings-panel">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
              <h2 className="font-display text-lg font-semibold">Email delivery (Brevo)</h2>
              <span className={`font-mono-label text-[10px] uppercase px-3 py-1 rounded ${emailCfg?.configured ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-testid="email-status-badge">
                {emailCfg?.configured ? 'Live' : 'Stubbed - key missing'}
              </span>
            </div>
            <p className="text-sm text-slate-500 mb-4">Paste your Brevo API key (starts with <code className="font-mono-label text-xs">xkeysib-</code>) and verified sender. Takes effect immediately - no restart needed.</p>
            <div className="space-y-3">
              <div>
                <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">API key {emailCfg?.api_key_masked && <span className="text-green-700">· current: {emailCfg.api_key_masked}</span>}</p>
                <Input data-testid="brevo-key-input" type="password" placeholder="xkeysib-..." value={emailForm.api_key} onChange={(e) => setEmailForm({ ...emailForm, api_key: e.target.value })} />
              </div>
              <div>
                <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Verified sender email</p>
                <Input data-testid="sender-email-input" placeholder={emailCfg?.sender_email || 'support@yourschool.com'} value={emailForm.sender_email} onChange={(e) => setEmailForm({ ...emailForm, sender_email: e.target.value })} />
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button data-testid="email-save-button" disabled={saveEmailMut.isPending || (!emailForm.api_key && !emailForm.sender_email)} onClick={() => saveEmailMut.mutate()} className="bg-slate-900 hover:bg-slate-800">Save credentials</Button>
                <Button data-testid="email-test-button" variant="outline" disabled={testEmailMut.isPending} onClick={() => testEmailMut.mutate()}>Send test email to me</Button>
                {emailCfg?.api_key_masked && (
                  <Button data-testid="email-clear-button" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50"
                    onClick={() => { if (window.confirm('Remove the stored API key? Email will fall back to stubbed mode.')) api.post('/settings/email', { clear_key: true }).then(() => { toast.success('API key removed'); qc.invalidateQueries({ queryKey: ['email-settings'] }); }); }}>
                    Remove key
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-400">Keys are stored server-side and never shown in full again. Announcements and fee reminders switch to real delivery automatically once configured.</p>
            </div>
          </div>
          <BrandingCard tenantId={user?.tenant_id || school?.id} />
          <TenantSettingsCard tenantId={user?.tenant_id || school?.id} />
          <IndiaPackCard tenantId={user?.tenant_id || school?.id} />
        </TabsContent>
        <TabsContent value="marketplace" className="mt-4">
          <MarketplaceTab tenantId={user?.tenant_id || school?.id} />
        </TabsContent>
      </Tabs>

      
    </div>
  );
}
